import { apiService } from './api'
import { RBAC_ENDPOINTS } from '../../api/endpoints'
import type {
  Role,
  RoleListItem,
  RoleDetail,
  Permission,
  PermissionCategory,
  User,
} from '../types/api'

/**
 * RBAC Service
 * Handles role and permission management API calls
 */

// Role Management

export interface CreateRoleData {
  name: string
  slug: string
  description: string
  permissionIds?: string[]
}

export interface UpdateRoleData {
  name?: string
  description?: string
}

export interface AssignPermissionsData {
  permissionIds: string[]
}

export interface AssignRolesData {
  roleIds: string[]
}

export interface RolesListResponse {
  roles: RoleListItem[]
  total: number
}

export interface PermissionsListResponse {
  permissions: Permission[]
  total: number
}

export interface PermissionCategoriesResponse {
  categories: PermissionCategory[]
}

export interface UserRolesResponse {
  roles: Role[]
}

/**
 * Get all roles with optional filtering
 */
export const getRoles = async (params?: {
  isSystem?: boolean
  search?: string
}): Promise<RolesListResponse> => {
  const queryParams = new URLSearchParams()
  if (params?.isSystem !== undefined) {
    queryParams.append('is_system', params.isSystem.toString())
  }
  if (params?.search) {
    queryParams.append('search', params.search)
  }

  const url = `${RBAC_ENDPOINTS.ROLES}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
  const response = await apiService.get<{ data: RolesListResponse }>(url)
  return response.data
}

/**
 * Get role details including permissions and user count
 */
export const getRoleById = async (roleId: string): Promise<RoleDetail> => {
  const response = await apiService.get<{ data: RoleDetail }>(RBAC_ENDPOINTS.ROLE_BY_ID(roleId))
  return response.data
}

/**
 * Create a new role
 */
export const createRole = async (data: CreateRoleData): Promise<Role> => {
  const response = await apiService.post<{ data: Role }>(RBAC_ENDPOINTS.ROLES, data)
  return response.data
}

/**
 * Update an existing role
 */
export const updateRole = async (roleId: string, data: UpdateRoleData): Promise<Role> => {
  const response = await apiService.put<{ data: Role }>(RBAC_ENDPOINTS.ROLE_BY_ID(roleId), data)
  return response.data
}

/**
 * Delete a role (system roles cannot be deleted)
 */
export const deleteRole = async (roleId: string): Promise<void> => {
  await apiService.delete(RBAC_ENDPOINTS.ROLE_BY_ID(roleId))
}

/**
 * Assign permissions to a role (bulk replacement)
 */
export const assignPermissionsToRole = async (
  roleId: string,
  permissionIds: string[]
): Promise<void> => {
  await apiService.post(RBAC_ENDPOINTS.ROLE_PERMISSIONS(roleId), {
    permission_ids: permissionIds,
  })
}

/**
 * Revoke a single permission from a role
 */
export const revokePermissionFromRole = async (
  roleId: string,
  permissionId: string
): Promise<void> => {
  await apiService.delete(RBAC_ENDPOINTS.REVOKE_ROLE_PERMISSION(roleId, permissionId))
}

/**
 * Get users assigned to a specific role
 */
export const getUsersByRole = async (roleId: string): Promise<User[]> => {
  const response = await apiService.get<{ data: { users: User[] } }>(
    RBAC_ENDPOINTS.ROLE_USERS(roleId)
  )
  return response.data.users
}

// Permission Management

/**
 * Get all permissions with optional filtering
 */
export const getPermissions = async (params?: {
  category?: string
  resource?: string
  isSystem?: boolean
  search?: string
}): Promise<PermissionsListResponse> => {
  const queryParams = new URLSearchParams()
  if (params?.category) {
    queryParams.append('category', params.category)
  }
  if (params?.resource) {
    queryParams.append('resource', params.resource)
  }
  if (params?.isSystem !== undefined) {
    queryParams.append('is_system', params.isSystem.toString())
  }
  if (params?.search) {
    queryParams.append('search', params.search)
  }

  const url = `${RBAC_ENDPOINTS.PERMISSIONS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
  const response = await apiService.get<{ data: PermissionsListResponse }>(url)
  return response.data
}

/**
 * Get permission categories with counts
 */
export const getPermissionCategories = async (): Promise<PermissionCategory[]> => {
  const response = await apiService.get<{ data: PermissionCategoriesResponse }>(
    RBAC_ENDPOINTS.PERMISSION_CATEGORIES
  )
  return response.data.categories
}

/**
 * Create a new custom permission (future extensibility)
 */
export const createPermission = async (data: {
  code: string
  resource?: string
  action?: string
  description: string
  category?: string
}): Promise<Permission> => {
  const response = await apiService.post<{ data: Permission }>(RBAC_ENDPOINTS.PERMISSIONS, data)
  return response.data
}

// User Role Assignment

/**
 * Get roles assigned to a specific user
 */
export const getUserRoles = async (userId: string): Promise<Role[]> => {
  const response = await apiService.get<{ data: UserRolesResponse }>(
    RBAC_ENDPOINTS.USER_ROLES(userId)
  )
  return response.data.roles
}

/**
 * Assign roles to a user (replaces existing assignments)
 */
export const assignRolesToUser = async (userId: string, roleIds: string[]): Promise<void> => {
  await apiService.post(RBAC_ENDPOINTS.USER_ROLES(userId), {
    role_ids: roleIds,
  })
}

/**
 * Revoke a specific role from a user
 */
export const revokeRoleFromUser = async (userId: string, roleId: string): Promise<void> => {
  await apiService.delete(RBAC_ENDPOINTS.REVOKE_USER_ROLE(userId, roleId))
}
